import BreadcrumbsBlock from '@/blocks/BreadcrumbsBlock';
import TextImageAsideBlock from '@/blocks/TextImageAsideBlock';
import FancyHeading from '@/components/FancyHeading';
import HeadingWithIcon from '@/components/HeadingWithIcon';
import Prose from '@/components/Prose';
import { MEDIA_URL, REVALIDATE_TIME_IN_SEC } from '@/constants/env';
import { fadeIn, fadeInUp } from '@/constants/motions';
import { pages } from '@/content';
import mainLayout from '@/graphql/queries/mainLayout';
import page from '@/graphql/queries/page';
import FullWidthLayout from '@/layouts/FullWidthLayout';
import graphql from '@/lib/graphql';
import Image from '@/ui/Image';
import { motion } from 'framer-motion';
import Markdown from 'react-markdown';

export default function HseqPage({ mainLayout, page }) {
  const attributes = page?.attributes || {};
  const contentSection = attributes.contentSection || [];
  const metaData = attributes.metaData || {};
  return (
    <FullWidthLayout mainLayout={mainLayout} metaData={metaData}>
      <BreadcrumbsBlock items={[pages.home, pages.about, pages.hseq]} />
      <motion.section
        className="my-16 px-4"
        initial="hidden"
        transition={fadeInUp.transition}
        variants={fadeInUp}
        viewport={fadeInUp.viewport}
        whileInView="visible"
      >
        <div className="container-screen">
          <FancyHeading>{attributes.shortName || ''}</FancyHeading>
        </div>
      </motion.section>
      {contentSection[0]?.textImageAsideImage?.data?.attributes?.url ? (
        <TextImageAsideBlock
          image={MEDIA_URL.concat(contentSection[0].textImageAsideImage.data.attributes.url)}
          imageClassName="lg:aspect-square"
          imageDirection="left"
          title={
            <HeadingWithIcon icon={contentSection[0]?.icon}>
              {contentSection[0]?.textImageAsideTitle}
            </HeadingWithIcon>
          }
        >
          <Prose className="mt-6 prose-p:my-3">
            <Markdown>{contentSection[0]?.textImageAsideText || ''}</Markdown>
          </Prose>
        </TextImageAsideBlock>
      ) : null}
      <section className="my-16 px-4">
        <div className="container-screen">
          <motion.div
            className="grid items-center gap-8 lg:grid-cols-2"
            initial="hidden"
            transition={{
              ...fadeIn.transition,
              when: 'beforeChildren',
              staggerChildren: 0.4,
            }}
            variants={fadeIn}
            viewport={fadeIn.viewport}
            whileInView="visible"
          >
            <motion.div variants={fadeInUp}>
              <HeadingWithIcon icon={contentSection[4]?.icon}>
                {contentSection[4]?.title || ''}
              </HeadingWithIcon>
              <Prose className="mt-6 prose-p:my-3">
                <Markdown>{contentSection[4]?.textMultiImageAsideText || ''}</Markdown>
              </Prose>
            </motion.div>
            <motion.div className="relative max-w-screen-sm pb-10" variants={fadeInUp}>
              <div className="absolute bottom-0 h-20 w-full rounded-lg bg-primary-600"></div>
              <div className="flex justify-evenly gap-4 px-6">
                {contentSection[4]?.images?.data?.[0]?.attributes?.url ? (
                  <Image
                    alt="Quality certificate"
                    className="aspect-[3/4] h-full w-1/2 object-cover shadow-lg"
                    src={MEDIA_URL.concat(contentSection[4].images?.data[0].attributes.url)}
                  />
                ) : null}
                {contentSection[4]?.images?.data?.[1]?.attributes?.url ? (
                  <Image
                    alt="Quality certificate"
                    className="aspect-[3/4] h-full w-1/2 object-cover shadow-lg"
                    src={MEDIA_URL.concat(contentSection[4].images?.data[1].attributes.url)}
                  />
                ) : null}
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>
      <section className="my-16 px-4">
        <div className="container-screen">
          <motion.div
            initial="hidden"
            transition={fadeInUp.transition}
            variants={fadeInUp}
            viewport={fadeInUp.viewport}
            whileInView="visible"
          >
            {contentSection[1]?.itemImage?.data?.attributes?.url ? (
              <Image
                alt="HSEQ Quality"
                className="aspect-video w-full overflow-hidden rounded-xl object-cover shadow-lg md:h-96"
                src={MEDIA_URL.concat(contentSection[1].itemImage?.data.attributes.url)}
              />
            ) : null}
          </motion.div>
          <motion.div
            className="mt-8"
            initial="hidden"
            transition={{
              ...fadeInUp.transition,
              delay: 0.4,
            }}
            variants={fadeInUp}
            viewport={fadeInUp.viewport}
            whileInView="visible"
          >
            <HeadingWithIcon icon={contentSection[2]?.icon}>
              {contentSection[2]?.textImageAsideTitle || ''}
            </HeadingWithIcon>
            <Prose className="mt-6 max-w-full prose-p:my-3">
              <Markdown>{contentSection[2]?.textImageAsideText || ''}</Markdown>
            </Prose>
          </motion.div>
        </div>
      </section>
      <section className="my-16 px-4">
        <div className="container-screen">
          <motion.div
            className="grid items-center gap-8 lg:grid-cols-2"
            initial="hidden"
            transition={{
              ...fadeIn.transition,
              when: 'beforeChildren',
              staggerChildren: 0.4,
            }}
            variants={fadeIn}
            viewport={fadeIn.viewport}
            whileInView="visible"
          >
            <motion.div className="relative max-w-screen-sm pb-10" variants={fadeInUp}>
              <div className="absolute bottom-0 h-20 w-full rounded-lg bg-primary-600"></div>
              <div className="flex justify-evenly gap-4 px-6">
                {contentSection[3]?.images?.data?.[0]?.attributes?.url ? (
                  <Image
                    alt="Quality certificate"
                    className="aspect-[3/4] h-full w-1/2 object-cover shadow-lg"
                    src={MEDIA_URL.concat(contentSection[3].images?.data[0].attributes.url)}
                  />
                ) : null}
                {contentSection[3]?.images?.data?.[1]?.attributes?.url ? (
                  <Image
                    alt="Quality certificate"
                    className="aspect-[3/4] h-full w-1/2 object-cover shadow-lg"
                    src={MEDIA_URL.concat(contentSection[3].images?.data[1].attributes.url)}
                  />
                ) : null}
              </div>
            </motion.div>
            <motion.div variants={fadeInUp}>
              <HeadingWithIcon icon={contentSection[3]?.icon}>
                {contentSection[3]?.title || ''}
              </HeadingWithIcon>
              <Prose className="mt-6 prose-p:my-3">
                <Markdown>{contentSection[3]?.textMultiImageAsideText || ''}</Markdown>
              </Prose>
            </motion.div>
          </motion.div>
        </div>
      </section>
      <section className="my-16 px-4">
        <div className="container-screen">
          <motion.div
            className="grid items-center gap-8 lg:grid-cols-2"
            initial="hidden"
            transition={{
              ...fadeIn.transition,
              when: 'beforeChildren',
              staggerChildren: 0.4,
            }}
            variants={fadeIn}
            viewport={fadeIn.viewport}
            whileInView="visible"
          >
            <motion.div variants={fadeInUp}>
              <HeadingWithIcon icon={contentSection[5]?.icon}>
                {contentSection[5]?.title || ''}
              </HeadingWithIcon>
              <Prose className="mt-6 prose-p:my-3">
                <Markdown>{contentSection[5]?.textMultiImageAsideText || ''}</Markdown>
              </Prose>
            </motion.div>
            <motion.div className="relative max-w-screen-sm pb-10" variants={fadeInUp}>
              <div className="absolute bottom-0 h-20 w-full rounded-lg bg-primary-600"></div>
              <div className="flex justify-evenly gap-4 px-6">
                {(contentSection[5]?.images?.data || []).map((image, i) => (
                  <Image
                    key={i}
                    alt="Image"
                    className="aspect-[4/3] h-full w-1/2 object-cover shadow-lg"
                    src={MEDIA_URL.concat(image?.attributes?.url || '')}
                  />
                ))}
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </FullWidthLayout>
  );
}

export async function getStaticProps() {
  const { data: mainLayoutData } = await graphql(mainLayout);

  const { data: pageData } = await graphql(page, { slug: 'hseq' });

  return {
    props: {
      mainLayout: mainLayoutData.mainLayout.data,
      page: pageData.pages.data[0],
    },
    revalidate: parseInt(REVALIDATE_TIME_IN_SEC), // In seconds
  };
}
